import { useEffect, useState } from "react";
import { getDadosAlunos } from "@/services/report";
import { getAllEspecificas } from "@/services/especifica";
import { getTurma } from "@/services/turma";
import { Table, ConfigProvider, theme, Button } from "antd";
import { Select } from "antd";
import type { ColumnsType } from 'antd/es/table';
import { format } from "date-fns";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { mascaraCpf, mascaraMatricula, mascaraCelular } from "@/utils/masks";
import "@/styles/globals.css";


interface DadosAlunosItem {
  matricula: number;
  nome: string;
  cpf: string;
  email: string;
  dataNascimento: string;
  celular: string;
}

interface Especifica {
  id: number;
  nome: string;
}

export default function DadosAlunos() {

  const [especificas, setEspecificas] = useState<Especifica[]>([]);
  const [turmas, setTurmas] = useState<{ id: number; nome: string }[]>([]);
  const [filtroEspecifica, setFiltroEspecifica] = useState("");
  const [filtroTurma, setFiltroTurma] = useState("");

  useEffect(() => {
    getTurma().then((res) => setTurmas(res.data || []));
    getAllEspecificas().then((res) => setEspecificas(res.data || []));
  }, []);

  useEffect(() => {
    if (filtroTurma) {
      getAllEspecificas({ turmaId: filtroTurma }).then((res) => setEspecificas(res.data || []));
      setFiltroEspecifica("");
    } else {
      getAllEspecificas().then((res) => setEspecificas(res.data || []));
      setFiltroEspecifica("");
    }
  }, [filtroTurma]);
  const [loading, setLoading] = useState(false);
  const [dados, setDados] = useState<DadosAlunosItem[]>([]);

  const buscar = async () => {
    setLoading(true);
    const params: any = {};
    if (filtroTurma) {
      params.turmaId = filtroTurma;
    }
    if (filtroEspecifica) {
      params.especificaId = filtroEspecifica;
    }
    const res = await getDadosAlunos(params);
    setDados(res.data || []);
    setLoading(false);
  };

  const exportToExcel = () => {
    const exportData = dados.map(item => ({
      "Matrícula": mascaraMatricula(item.matricula.toString()),
      "Nome": item.nome,
      "CPF": item.cpf ? mascaraCpf(item.cpf) : "",
      "E-mail": item.email,
      "Data Nascimento": item.dataNascimento ? format(new Date(item.dataNascimento), "dd/MM/yyyy") : "",
      "Celular": item.celular ? mascaraCelular(item.celular) : "",
    }));

    // Cabeçalho dos filtros
    const turmaSelecionada = turmas.find(t => t.id === Number(filtroTurma));
    const especificaSelecionada = especificas.find(e => e.id === Number(filtroEspecifica));
    const nomeTurma = filtroTurma ? turmaSelecionada?.nome : "Todas";
    const nomeEspecifica = filtroEspecifica ? especificaSelecionada?.nome : "Todas";
    const header = [[`Turma: ${nomeTurma}`, `Especifica: ${nomeEspecifica}`]];
    const ws = XLSX.utils.aoa_to_sheet(header);
    XLSX.utils.sheet_add_json(ws, exportData, { origin: -1 });

    // Aplicar formatação de alinhamento às colunas
    if (ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let row = range.s.r + 1; row <= range.e.r; row++) { // +1 para pular header
        // Coluna A (Matrícula) - índice 0
        const cellAddressMatricula = XLSX.utils.encode_cell({ r: row, c: 0 });
        if (ws[cellAddressMatricula]) {
          if (!ws[cellAddressMatricula].s) ws[cellAddressMatricula].s = {};
          ws[cellAddressMatricula].s.alignment = { horizontal: 'right' };
        }
        // Coluna E (Data Nascimento) - definir como tipo Data
        const cellAddressData = XLSX.utils.encode_cell({ r: row, c: 4 });
        if (ws[cellAddressData]) {
          if (!ws[cellAddressData].s) ws[cellAddressData].s = {};
          ws[cellAddressData].t = 'd';
        }
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DadosAlunos");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), "dados_alunos.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16);
    doc.text("Dados dos Alunos", 14, 14);
    // Header dos filtros
    const turmaSelecionada = turmas.find(t => t.id === Number(filtroTurma));
    const especificaSelecionada = especificas.find(e => e.id === Number(filtroEspecifica));
    const nomeTurma = filtroTurma ? turmaSelecionada?.nome : "Todas";
    const nomeEspecifica = filtroEspecifica ? especificaSelecionada?.nome : "Todas";
    doc.setFontSize(12);
    doc.text(`Turma: ${nomeTurma}`, 14, 22);
    doc.text(`Especifica: ${nomeEspecifica}`, 80, 22);

    const head = [
      "Matrícula", "Nome", "CPF", "E-mail", "Data Nascimento", "Celular"
    ];

    const tableData = dados.map(item => [
      mascaraMatricula(item.matricula.toString()),
      item.nome || "",
      item.cpf ? mascaraCpf(item.cpf) : "",
      item.email || "",
      item.dataNascimento ? format(new Date(item.dataNascimento), "dd/MM/yyyy") : "",
      item.celular ? mascaraCelular(item.celular) : "",
    ]);

    autoTable(doc, {
      head: [head],
      body: tableData,
      startY: 24,
      styles: { fontSize: 9 },
      margin: { left: 10, right: 10 },
      tableWidth: 'auto',
      columnStyles: {
        0: { cellWidth: 20, halign: 'right' }, // Matrícula - alinhada à direita
        1: { cellWidth: 50 }, // Nome
        2: { cellWidth: 30 }, // CPF
        3: { cellWidth: 60 }, // E-mail
        4: { cellWidth: 25 }, // Data Nascimento
        5: { cellWidth: 30 }, // Celular
      },
    });

    doc.save("dados_alunos.pdf");
  };

  const columns: ColumnsType<DadosAlunosItem> = [
    { 
      title: "Matrícula", 
      dataIndex: "matricula", 
      key: "matricula",
      align: "right",
      className: "text-right",
      render: (matricula: number) => mascaraMatricula(matricula.toString())
    },
    { title: "Nome", dataIndex: "nome", key: "nome" },
    { 
      title: "CPF", 
      dataIndex: "cpf", 
      key: "cpf", 
      render: (cpf: string) => cpf ? mascaraCpf(cpf) : "" 
    },
    { title: "E-mail", dataIndex: "email", key: "email" },
    {
      title: "Data Nascimento",
      dataIndex: "dataNascimento",
      key: "dataNascimento",
      render: (date: string) => (date ? format(new Date(date), "dd/MM/yyyy") : ""),
    },
    { 
      title: "Celular", 
      dataIndex: "celular", 
      key: "celular",
      render: (celular: string) => celular ? mascaraCelular(celular) : ""
    },
  ];

  return (
    <>
      <div className="mb-8">
        <span className="text-white text-2xl">Dados dos Alunos</span>
      </div>
      <ConfigProvider theme={{ algorithm: [theme.compactAlgorithm, theme.darkAlgorithm] }}>
        <div className="flex gap-4 items-end mb-8">
          <Select
            value={filtroTurma}
            onChange={setFiltroTurma}
            style={{ width: 200 }}
            allowClear
            placeholder="Turma"
            options={[
              { value: "", label: "Todas Turmas" },
              ...turmas.map((t) => ({ value: t.id, label: t.nome })),
            ]}
          />
          <Select
            value={filtroEspecifica}
            onChange={setFiltroEspecifica}
            style={{ width: 200 }}
            allowClear
            placeholder="Especifica"
            options={[
              { value: "", label: "Todas Específicas" },
              ...especificas.map((e) => ({ value: e.id, label: e.nome })),
            ]}
          />
          <Button type="primary" loading={loading} onClick={buscar}>
            Buscar
          </Button>
          <Button onClick={exportToExcel} disabled={dados.length === 0}>Exportar Excel</Button>
          <Button onClick={exportToPDF} disabled={dados.length === 0}>Exportar PDF</Button>
        </div>
        <Table
          columns={columns}
          dataSource={dados}
          rowKey={(row) => row.matricula.toString()}
          pagination={{ pageSize: 20 }}
          locale={{ emptyText: "Nenhum dado encontrado." }}
        />
      </ConfigProvider>
    </>
  );
}
